import moment from 'moment';
import {isValidDate} from './validators';

export function normalizeEdges(data) {
    // Null check
    if (!data) {
        return data;
    }

    // Map
    if (data['edges'] && Array.isArray(data['edges'])) {
        return data['edges'].map(({node}) => {
            let key, newNode = {};

            for (key in node) {
                if (node.hasOwnProperty(key)) {
                    newNode[key] = normalizeEdges(node[key]);
                }
            }

            return newNode;
        });
    }

    // This is a little bit tricky since both arrays and dictionaries are of type 'object'
    if (Array.isArray(data)) {
        return data.map(item => normalizeEdges(item));
    }

    if (typeof data === 'object') {
        let key, newNode = {};

        for (key in data) {
            if (data.hasOwnProperty(key)) {
                newNode[key] = normalizeEdges(data[key]);
            }
        }

        return newNode;
    }

    return data;
}


export function normalizeObject(obj) {
    let key,
        newObject = {};

    for (key in obj) {
        if (obj.hasOwnProperty(key)) {
            newObject[key] = normalizeEdges(obj[key]);
        }
    }

    return newObject;
}

/**
 * Flattens an array with object 1 level deep!
 * @param array
 */
export function flattenArray(array) {
    return array.map(row => {
        let newRow = {};

        Object.keys(row).map(key => {
            let value = row[key];

            if (value && typeof value === 'object') {
                if (Object.keys(value).length === 0) {
                    newRow = Object.assign(newRow, {[key]: null});
                }
                else {
                    Object.keys(value).map(k => {
                        let _key = `${key}_${k}`;

                        newRow = Object.assign(newRow, {[_key]: value[k]});

                        return k;
                    });
                }
            }
            else {
                newRow = Object.assign(newRow, {[key]: value});
            }

            return key;
        });

        return newRow;
    });
}


const parseValue = (value) => {
    if (value === 'true') {
        return true;
    }

    if (value === 'false') {
        return false;
    }

    if (value === 'None' || value === '') {
        return null;
    }

    if (Number(value)) {
        return Number(value);
    }

    return value;
};


export function formToJSON(form) {
    // Convert a form to json
    // Only include fields with explicit 'name'-property!

    if (!form) {
        // Todo: Maybe throw error?
        return null;
    }

    const json = {};

    // TODO: Add support for more types (radio buttons etc)
    const inputElements = form.querySelectorAll('input,textarea,select,button');

    let n = 0,
        nn = inputElements.length,
        element;

    for (n; n < nn; n++) {
        element = inputElements[n];
        // Check type
        if (element.name && element.name !== '') {
            if (element.type === 'checkbox') {
                json[element.name] = !!element.checked; // Force true or false
            }
            else {
                json[element.name] = parseValue(element.value);
            }
        }
    }

    return json;
}


export function isEmptyObject(obj) {
    if (!obj) {
        return true;
    }

    let key;

    for (key in obj) {
        if (obj.hasOwnProperty(key)) {
            return false;
        }
    }

    return JSON.stringify(obj) === JSON.stringify({});
}


export const formatDate = (year, month, date, dateFormat = 'YYYY-MM-DD') => {
    if (!isValidDate(year, month, date)) {
        return null;
    }

    let _date = new Date(parseInt(year, 10), parseInt(month, 10) - 1, parseInt(date, 10));

    return moment(_date).format(dateFormat);
};

const getUserNameDefaultOptions = {fallbackOnEmail: true, guessFromEmail: false, firstNameOnly: false};

export const getUserName = (user, {fallbackOnEmail, guessFromEmail, firstNameOnly} = getUserNameDefaultOptions) => {
    if (!user || isEmptyObject(user)) {
        return '';
    }

    if (user.firstName) {
        return `${user.firstName}${user.lastName && !firstNameOnly ? ' ' + user.lastName : ''}`;
    }

    if (fallbackOnEmail && user.email) {
        return user.email;
    }

    if (guessFromEmail === true && user.email) {
        return capitalizeFirstLetter(user.email.split('@')[0]);
    }

    return '';
};

export const getUserInitials = (user) => {
    if (!user || isEmptyObject(user) || !user.firstName || !user.lastName) {
        return '';
    }

    return `${user.firstName[0]}${user.lastName[0]}`.toUpperCase();
};

export function capitalizeFirstLetter(string) {
    return string.charAt(0).toUpperCase() + string.slice(1).toLowerCase();
}

export const displayValue = (value) => {
    if (typeof value === 'object') {
        return JSON.stringify(value);
    }

    if (typeof value === 'string') {
        return value;
    }

    if (typeof value === 'boolean') {
        return value ? 'TRUE' : 'FALSE';
    }

    return 'Unknown';
};

export const tryParseJSON = (jsonString, fallback = {}) => {
    try {
        return JSON.parse(jsonString);
    }
    catch (error) {
        return fallback;
    }
};

export const roundValue = (value, decimals) => Math.round(Math.pow(10, decimals) * value) / (Math.pow(10, decimals));

export const capValue = (value, minValue, maxValue) => Math.max(Math.min(value, maxValue), minValue);

export const roundStep = (value, step) => Math.round(value * (1.0 / step)) * step;

export const approximateYearsAtFirmForEmployment = (employment, decimals = 1) => {
    if (isEmptyObject(employment)) {
        return -1;
    }
    const {yearsAtFirm, monthsAtFirm, daysAtFirm} = employment;

    const approx = yearsAtFirm + monthsAtFirm / 12 + daysAtFirm / 365;

    return roundValue(approx, decimals);
};


const daysBetweenDates = (first, second) => {
    // Take the difference between the dates and divide by milliseconds per day.
    // Round to nearest whole number to deal with DST.
    return Math.round((second - first) / (1000 * 60 * 60 * 24));
};

export const daysFromToday = (dateString) => {
    const first = new Date(dateString);
    const second = new Date();

    return daysBetweenDates(first, second);
};
